import {
  AccountBookTwoTone,
  CalculatorOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons';
import { Alert, Result } from 'antd';
import BigNumber from 'bignumber.js';
import React from 'react';
import { APIError } from '../../../../api/types';
import NumericKeyboard from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import Price from '@/components/Price';
import { PayQRScanner, PayQRScannerProps } from '@/components/QRScanner';
import { isGrouponPaymentChannel, PaymentChannel } from '@/models/payment';
import { UserFullInfo } from '@/models/user';
import { playSound } from '@/utils/sound';
import BalanceConsume from '../BalanceConsume';
import CashCalc from '../CashCalc';
import PaymentChannelSelector from '../PaymentChannelSelector';
import styles from '../style.less';
import { IconDY, IconMT } from '@/components/Icons';
import { useSettlementStore } from '../store/useSettlementStore';

type Props = {
  user: UserFullInfo;
  requestLoading: boolean;
  requestError: APIError;
  balancePayAvailable?: boolean;
  onRequestCodePayTradeCancel: PayQRScannerProps['onRequestCodePayTradeCancel'];
  onSubmit: () => void;
};

/**
 * POS 支付区域面板组件
 * 
 * 已解耦 antd Form，直接通过 useSettlementStore 的选择器（Selectors）订阅支付渠道、扫码输入及应收款项，
 * 仅在当前组件关注的状态发生变化时触发局部的组件重渲染。
 */
const Payment: React.FC<Props> = ({
  user,
  requestLoading,
  requestError,
  balancePayAvailable = true,
  onRequestCodePayTradeCancel,
  onSubmit,
}: Props) => {
  // 从 Zustand Store 细粒度订阅支付与金额相关状态
  const actualPrice = useSettlementStore(s => s.actualPrice);
  const totalPrice = useSettlementStore(s => s.totalPrice);
  const balancePrice = useSettlementStore(s => s.balancePrice);
  const setBalancePrice = useSettlementStore(s => s.setBalancePrice);

  const paymentChannel = useSettlementStore(s => s.paymentChannel);
  const setPaymentChannel = useSettlementStore(s => s.setPaymentChannel);
  const authCode = useSettlementStore(s => s.authCode);
  const setAuthCode = useSettlementStore(s => s.setAuthCode);
  const cashReceived = useSettlementStore(s => s.cashReceived);
  const setCashReceived = useSettlementStore(s => s.setCashReceived);

  if (!actualPrice) {
    return <Result icon={<DollarCircleOutlined />} title="请输入待收款金额" />;
  }

  return (
    <>
      <div className={styles.payMethodSelectorContainer}>
        <h2>
          <AccountBookTwoTone twoToneColor={'#eb2f96'} /> 待收款:{' '}
          <Price value={new BigNumber(actualPrice).plus(balancePrice || 0)} />
        </h2>

        {balancePayAvailable ? (
          <BalanceConsume
            user={user?.user}
            actualPrice={actualPrice}
            totalPrice={totalPrice}
            value={balancePrice}
            onChange={val => setBalancePrice(val)}
          />
        ) : null}

        <PaymentChannelSelector
          actualPrice={new BigNumber(actualPrice)}
          balancePayAvailable={balancePayAvailable}
          user={user ? user.user : null}
          value={paymentChannel}
          onChange={channel => {
            playSound('click');
            setPaymentChannel(channel);
          }}
        />
      </div>

      {paymentChannel === PaymentChannel.Online && (
        <div style={{ paddingTop: 20 }}>
          <PayQRScanner
            loading={requestLoading}
            error={requestError}
            onRequestCodePayTradeCancel={onRequestCodePayTradeCancel}
            onChange={code => {
              setAuthCode(code);
              onSubmit();
            }}
          />
        </div>
      )}

      {paymentChannel === PaymentChannel.Cash && (
        <>
          <h3 className={styles.calcHeader}>
            <div>
              <CalculatorOutlined /> 收款计算器
            </div>
            <CashCalc
              actualPrice={new BigNumber(actualPrice || 0)}
              cashReceived={new BigNumber(cashReceived || 0)}
            />
          </h3>
          <NumericKeyboard
            value={cashReceived ? Number(cashReceived) : undefined}
            parseValueToNumber
            precision={2}
            className={styles.calcKeyboard}
            placeholder={'请输入收款金额'}
            onChange={val => setCashReceived(val ? Number(val) : null)}
            onComplete={() => {
              onSubmit();
            }}
          />
        </>
      )}

      {(paymentChannel === PaymentChannel.INVALID || isGrouponPaymentChannel(paymentChannel)) && (
        <>
          {isGrouponPaymentChannel(paymentChannel) && (
            <div className={styles.grouponContainer}>
              <Alert type="warning" showIcon message="请确保已经在对应平台完成验券" />
              <div className={styles.grouponSelect}>
                <div
                  className={`${styles.grouponItem} ${
                    paymentChannel === PaymentChannel.Groupon_MT ? styles.active : ''
                  }`}
                  onClick={() => {
                    playSound('click');
                    setPaymentChannel(PaymentChannel.Groupon_MT);
                  }}>
                  <span className={styles.grouponItemContent}>
                    <IconMT /> 美团
                  </span>
                  <CheckCircleOutlined className={styles.grouponItemActiveIndicator} />
                </div>
                <div
                  className={`${styles.grouponItem} ${
                    paymentChannel === PaymentChannel.Groupon_DY ? styles.active : ''
                  }`}
                  onClick={() => {
                    playSound('click');
                    setPaymentChannel(PaymentChannel.Groupon_DY);
                  }}>
                  <span className={styles.grouponItemContent}>
                    <IconDY /> 抖音
                  </span>
                  <CheckCircleOutlined className={styles.grouponItemActiveIndicator} />
                </div>
              </div>
            </div>
          )}
          <h3 className={styles.invalidPaymentHeader}>
            <div>
              <CalculatorOutlined /> 交易确认
            </div>
          </h3>
          <div className="text-center">
            <PosButton
              type="primary"
              onClick={() => {
                onSubmit();
              }}>
              本单无需额外收款，请核对无误后点此确认
            </PosButton>
          </div>
        </>
      )}
    </>
  );
};

export default Payment;
